<script setup>
import {computed, onMounted, ref} from "vue";
import QrcodeVue from "qrcode.vue";
import { useHead } from "@unhead/vue";
import axios from "axios";
import { useToast } from "vue-toast-notification";
import { appleWalletIcon, googleWalletIcon, logo } from "@/assets/images/imageImports.js";
import { captureError, trackEvent } from "@/analytics";

const toast = useToast();

// Renders a specific customer's wallet card from localStorage, no unique
// public content per URL, so keep it out of search results.
useHead({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] });

const userCardData = computed(() => {
    let userCard = window.localStorage.getItem('user_card');
    if (userCard) {
        return JSON.parse(userCard);
    }

    return null;
});

const deviceType = ref('');
const detectDeviceType = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera

    if (/iPad|iPhone|iPod/.test(ua) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document)) {
        return 'apple'
    } else if (/android/i.test(ua)) {
        return 'android'
    } else {
        return 'unknown'
    }
}

const isApple = computed(() => deviceType.value === 'apple');
const isAndroid = computed(() => deviceType.value === 'android');
const isUnknownDevice = computed(() => deviceType.value === 'unknown');

onMounted(() => {
    deviceType.value = detectDeviceType()
})

const addToAppleWallet = async () => {
    const code = userCardData.value?.code;
    if (!code) {
        console.warn('[AppleWallet] addToAppleWallet called without a card code');
        return;
    }
    const url = `/api/user-card/apple-wallet-pass/${code}`;
    // Correlates this attempt across console/PostHog logs and the matching
    // backend request log (see X-Wallet-Request-Id in UserCardController).
    const requestId = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
    console.log('[AppleWallet] Starting download', { requestId, url, userAgent: navigator.userAgent, deviceType: deviceType.value });
    trackEvent('apple_wallet_download_started', { requestId, userAgent: navigator.userAgent, deviceType: deviceType.value });
    try {
        // Validate first so a server error surfaces a real message instead of
        // Safari's generic "cannot download this file", then navigate to the
        // real endpoint (not a blob: URL), Safari only recognizes the
        // application/vnd.apple.pkpass response and offers to add it to
        // Wallet when it comes from an actual network navigation.
        const response = await axios.get(url, {
            responseType: 'blob',
            headers: { 'X-Wallet-Request-Id': requestId },
        });
        console.log('[AppleWallet] Server generated pass', {
            requestId,
            status: response.status,
            contentType: response.headers?.['content-type'],
            sizeBytes: response.data?.size,
        });
        trackEvent('apple_wallet_download_succeeded', { requestId, sizeBytes: response.data?.size });
        window.location.href = url;
    } catch (error) {
        let message = error.message;
        const data = error?.response?.data;
        if (data instanceof Blob) {
            try {
                message = JSON.parse(await data.text())?.message || message;
            } catch { /* not JSON, keep the default message */ }
        } else if (data?.message) {
            message = data.message;
        }
        console.error('[AppleWallet] Failed to generate pass', { requestId, status: error?.response?.status, message });
        toast.error("We couldn't add your card to Apple Wallet. Please try again.");
        trackEvent('apple_wallet_download_failed', { requestId, status: error?.response?.status, message });
        captureError(error, {
            context: 'add_to_apple_wallet',
            requestId,
            endpoint: url,
            status: error?.response?.status,
            message,
        });
    }
}

const addToGoogleWallet = () => {

}

</script>

<template>
<div  class="d-flex flex-column align-items-center justify-content-center vh-100">
    <div class="d-flex flex-column align-items-center user-card" v-if="userCardData">
        <div class="d-flex justify-content-between m-3 w-100">
            {{userCardData.name}}
            <img :src="logo" alt="wincard logo" class="client-card-logo" />
        </div>
        <qrcode-vue :value="userCardData.code" :size="150"></qrcode-vue>
    </div>
    <div class="d-flex align-items-center justify-content-center mt-3">
        <div>
            <img v-if="isApple || isUnknownDevice" class="apple-wallet-btn" @click="addToAppleWallet" :src="appleWalletIcon" :alt="$t('publicCreateCard.createAddToWallet')" />
        </div>
        <div>
            <img v-if="isAndroid || isUnknownDevice" class="google-wallet-btn" @click="addToGoogleWallet" :src="googleWalletIcon" :alt="$t('publicCreateCard.createAddToWallet')" />
            <span v-if="isGeneratingGoogleWallet"></span>
        </div>
    </div>
</div>
</template>

<style scoped>
.user-card {
    min-width: 250px;
    min-height: 250px;
    border: solid 1px gray;
    border-radius: 5px;
    padding: 10px;
    background-color: #313645;
    color: white;
}

.client-card-logo {
    width: 22px;
    height: 23px;
}

.apple-wallet-btn,
.google-wallet-btn {
    display: flex;
    justify-content: center;
    align-items: center;
    border: none;
    cursor: pointer;
    background-color: transparent;
    padding: 0;
    width: 121px;
    height: 33px;
}

.apple-wallet-btn img,
.google-wallet-btn img {
    width: 121px;
    height: 33.43px;
    cursor: pointer;
}
</style>
